import React from "react";
import styled from "styled-components";
import { PUBLIC_BOTS_LOADING_ID } from "testIds";
import Skeleton from "common/NewLook/Skeleton.tsx";
import PageIntroActions from "features/bots/NewLook/PageIntroActions";
import { newLookCardSurface } from "features/bots/NewLook/newLookStyles";
import NewLookPublicBotCardSkeleton from "features/bots/PublicBots/NewLookPublicBotCardSkeleton.tsx";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook/layout";

interface NewLookPublicBotsLoadingProps {
  isBottomNavigator: boolean;
  /** Narrower screens: a filters button instead of the filters column. */
  useFiltersChips: boolean;
}

const CARDS = Array.from({ length: 6 }, (_, index) => index);

/**
 * New look: the public bots page while its first request runs — the page's
 * toolbar and cards as placeholders where the real ones will be (no filters
 * or empty state yet, so nothing claims there are no bots).
 */
const NewLookPublicBotsLoading = ({
  isBottomNavigator,
  useFiltersChips,
}: NewLookPublicBotsLoadingProps) => {
  if (isBottomNavigator) {
    return (
      <PhoneList aria-busy="true" data-testid={PUBLIC_BOTS_LOADING_ID}>
        {CARDS.slice(0, 3).map((index) => (
          <NewLookPublicBotCardSkeleton key={index} />
        ))}
      </PhoneList>
    );
  }
  return (
    <Wrapper aria-busy="true" data-testid={PUBLIC_BOTS_LOADING_ID}>
      <PageIntroActions
        actions={
          <>
            <Skeleton width={220} height={32} radius={8} />
            {useFiltersChips && <Skeleton width={88} height={32} radius={8} />}
            <Skeleton width={124} height={32} radius={999} />
          </>
        }
        deps={[useFiltersChips]}
      />
      <Content>
        <Grid>
          {CARDS.map((index) => (
            <NewLookPublicBotCardSkeleton key={index} />
          ))}
        </Grid>
      </Content>
      {!useFiltersChips && (
        <FiltersColumn aria-hidden={true}>
          <Skeleton width={64} height={14} />
          {[0, 1, 2, 3].map((index) => (
            <FiltersGroup key={index}>
              <Skeleton width="40%" height={10} />
              <Skeleton width="100%" height={32} radius={8} />
            </FiltersGroup>
          ))}
        </FiltersColumn>
      )}
    </Wrapper>
  );
};

export default React.memo(NewLookPublicBotsLoading);

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 200px;
`;
const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} 16px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
  align-content: start;
  gap: 16px;
`;
const FiltersColumn = styled.div`
  ${newLookCardSurface};
  flex: none;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 300px;
  margin: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} 0 0;
  padding: 20px 16px;
`;
const FiltersGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const PhoneList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
`;
